// React
import { ReactElement, useEffect, useState } from 'react';
// Types
import { Slice } from '@utmb/types/slice-handler';
import { RacePreregistrationOpenView, SlicePreregistrationView } from '@utmb/types/index';
// Styles
import styles from './preregistration.module.scss';
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
import { useUser } from '@utmb/contexts/AuthContext';
import { useTenancy } from '@utmb/contexts/TenancyContext';
import { useRouter } from 'next/router';
import { getRaceCategoryColor } from '@utmb/utils/race-categories-color';
import { Image } from '@utmb/design-system/image/Image';
import { CloudinaryTransformEnum } from '@utmb/types/cloudinary-transform.enum';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { Font } from '@utmb/design-system/text/font';
import { useTranslation } from 'next-i18next';
import { usePreference } from '@utmb/contexts/PreferenceContext';
import { RegistrationStatusEnum } from '@utmb/types/enum/RegistrationStatusEnum';
import { Icon } from '@utmb/design-system/icons/Icon';
import { IconsEnum } from '@utmb/types/icons';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { PreregistrationForm } from './PreregistrationForm';
import { Button } from '@utmb/design-system/button/Button';
import { BoostReasonEnum } from '@utmb/types/enum/BoostReasonEnum';
import { SimpleStatusEnum } from '@utmb/types/enum/SimpleStatusEnum';
import ImageNext from 'next/image';
import { Stats } from './components/Stats';
import { ButtonRemake } from '@utmb/design-system/button-remake/ButtonRemake';
import { Insert } from '@utmb/design-system/insert/insert';

export const Preregistration = (props: Slice<SlicePreregistrationView>): ReactElement => {
    const { id } = props;

    const { t } = useTranslation();
    const { isAuthenticated, getToken, login } = useUser();
    const { tenantId } = useTenancy();
    const { locale } = useRouter();
    const {
        preferences: { preferredUnit },
    } = usePreference();

    //const [isLoading, setIsLoading] = useState(false);
    const [isDetailsOpen, setDetailsOpen] = useState(false);
    const [preregistrations, setPreregistrations] = useState<RacePreregistrationOpenView[]>([]);
    const [formOpen, setFormOpen] = useState(0);

    async function handleCancelRace(raceYearId: number): Promise<void> {
        if (confirm(t('races.preregistration.confirmCancel'))) {
            const token = await getToken();

            fetchTheAPI({
                isClient: true,
                tenantId,
                bearer: token,
                method: FetchMethodsEnum.DELETE,
                path: `/registration/pre-register-race-year/${raceYearId}`,
            })
                .then(() => {
                    setDetailsOpen(false);
                    getPreregistrations();
                })
                .catch((error) => {
                    alert(error);
                });
        }
    }

    async function getPreregistrations(): Promise<void> {
        //setIsLoading(true);
        const jwt = await getToken();

        const preregs = (await fetchTheAPI({
            tenantId,
            path: `/registration/pre-registrations?lang=${locale}`,
            bearer: jwt ?? '',
        })) as RacePreregistrationOpenView[];

        setPreregistrations(preregs);
        //setIsLoading(false);
    }

    useEffect(() => {
        getPreregistrations().then();
    }, [isAuthenticated]);

    return (
        <div id={id} className={`${styles.container} container`}>
            <ul>
                {preregistrations?.map((prereg) => {
                    const category = prereg.raceCategory;
                    return (
                        <li
                            key={`prereg${prereg.raceYearId}`}
                            className={[
                                styles.item,
                                prereg.canRegister || !isAuthenticated || (prereg.registration || prereg.hasPrio ? undefined : styles.disabled),
                                formOpen === prereg.raceYearId ? styles.formOpen : undefined,
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        >
                            <div
                                className={styles.card}
                                style={{ borderColor: prereg.raceCategory ? `var(${getRaceCategoryColor(prereg.raceCategory)})` : undefined }}
                            >
                                <div className={styles.image_wrapper}>
                                    {prereg.picture ? (
                                        <Image layout="fill" objectFit="cover" image={prereg.picture} addOptions={[CloudinaryTransformEnum.TRIM]} />
                                    ) : null}
                                </div>
                                {prereg.hasPrio ? (
                                    <div className={styles.priorityInsert}>
                                        <Insert
                                            skewDirection="left"
                                            label={t('races.preregistration.priority.title')}
                                            backgroundColor={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                                            color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                        />
                                    </div>
                                ) : null}
                                <div className={styles.card_content}>
                                    {category && <PiCategoryLogo piCategory={category} height={25} isDark={true} />}
                                    <div className={styles.race_name_wrapper}>
                                        {prereg.logo ? (
                                            <div className={styles.wrap_logo}>
                                                <Image
                                                    layout="fixed"
                                                    objectFit="contain"
                                                    width={172}
                                                    height={50}
                                                    image={prereg.logo}
                                                    alt={prereg.raceName}
                                                    className={styles.logo}
                                                />
                                            </div>
                                        ) : (
                                            <Font.FuturaBold
                                                className={styles.raceName}
                                                style={{
                                                    color: prereg.raceCategory
                                                        ? `var(${getRaceCategoryColor(prereg.raceCategory)})`
                                                        : 'var(--utmb-color-blue-light)',
                                                }}
                                                mobile="32"
                                                desktop="48"
                                                as="h3"
                                            >
                                                {prereg.raceName ?? ''}
                                            </Font.FuturaBold>
                                        )}
                                    </div>

                                    <Stats preferredUnit={preferredUnit} prereg={prereg} />
                                </div>

                                <div className={styles.card_extends + ' ' + (prereg.hasPrio ? styles.priorityGap : '')}>
                                    {prereg.registration ? (
                                        <>
                                            {[RegistrationStatusEnum.CANCELLED, RegistrationStatusEnum.PREREGISTERED_CANCELLED].includes(
                                                prereg.registration.status
                                            ) && (
                                                <div className={styles.card_status}>
                                                    <Font.OxaniumBold mobile="16" desktop="18" className={styles.statusCancelled}>
                                                        {t(`myutmb:myUtmb.registration.status.${prereg.registration.status}`)}
                                                    </Font.OxaniumBold>
                                                </div>
                                            )}
                                            {[
                                                RegistrationStatusEnum.REGISTERED,
                                                RegistrationStatusEnum.PREREGISTERED,
                                                RegistrationStatusEnum.PREREGISTERED_DRAW_KO,
                                                RegistrationStatusEnum.PREREGISTERED_DRAW_OK,
                                                RegistrationStatusEnum.PREREGISTERED_WAITING,
                                            ].includes(prereg.registration.status) && (
                                                <>
                                                    <div className={styles.card_status}>
                                                        <Font.OxaniumBold
                                                            mobile="16"
                                                            desktop="18"
                                                            className={`${styles.statusPreregistered} ${
                                                                prereg.registration.status === RegistrationStatusEnum.PREREGISTERED_DRAW_OK
                                                                    ? styles.statusPreregistered_Ok
                                                                    : prereg.registration.status === RegistrationStatusEnum.PREREGISTERED_DRAW_KO
                                                                    ? styles.statusPreregistered_Ko
                                                                    : ''
                                                            }`}
                                                        >
                                                            {t(`races.preregistration.status.${prereg.registration.status}`)}
                                                        </Font.OxaniumBold>
                                                    </div>
                                                    {prereg.registration.boostPct && (prereg.registration.hasBoost || prereg.boostUtmbGo) ? (
                                                        <Font.FuturaMedium as="li" mobile="14" desktop="16" className={styles.ctrl}>
                                                            {t(
                                                                `races.preregistration.${
                                                                    !prereg.registration.hasBoost
                                                                        ? 'withoutBoost'
                                                                        : prereg.registration.boostReason === BoostReasonEnum.LOYALTY
                                                                        ? 'withBoostLoyalty'
                                                                        : prereg.registration.boostReason === BoostReasonEnum.CUSTOM_CODE
                                                                        ? 'withBoostLocal'
                                                                        : 'withBoost'
                                                                }`,
                                                                {
                                                                    pct: prereg.registration.boostPct,
                                                                }
                                                            )}
                                                        </Font.FuturaMedium>
                                                    ) : null}
                                                    {prereg.registration.status === RegistrationStatusEnum.PREREGISTERED_DRAW_OK ? (
                                                        <>
                                                            {prereg.url ? (
                                                                <Button.Default
                                                                    color={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                                                                    bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                                    style={{ marginTop: '1rem' }}
                                                                    type="button"
                                                                    label={t('races.preregistration.finalizeButton')}
                                                                    onPress={() => (location.href = prereg.url!)}
                                                                />
                                                            ) : null}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {prereg.registration.status === RegistrationStatusEnum.PREREGISTERED && prereg.isOpen ? (
                                                                <a
                                                                    href="#"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        handleCancelRace(prereg.raceYearId);
                                                                    }}
                                                                    className={styles.cancel_link}
                                                                >
                                                                    <Font.FuturaBook
                                                                        color={getColorStyle(ColorTypesEnum.RED)}
                                                                        mobile="14"
                                                                        desktop="16"
                                                                        as="p"
                                                                        className={styles.card_information_text}
                                                                    >
                                                                        {t('races.preregistration.msgToCancel')}
                                                                        &nbsp;
                                                                        <Icon variant={IconsEnum.NO} color={getColorStyle(ColorTypesEnum.RED)} />
                                                                    </Font.FuturaBook>
                                                                </a>
                                                            ) : null}
                                                        </>
                                                    )}
                                                    {prereg.registration.grp || prereg.registration.status === RegistrationStatusEnum.PREREGISTERED_DRAW_OK ? (
                                                        <a
                                                            href="#"
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                setDetailsOpen(!isDetailsOpen);
                                                            }}
                                                        >
                                                            <Font.FuturaMedium as="span" mobile="14" desktop="16">
                                                                {t('races.preregistration.details')}
                                                                &nbsp;
                                                                <Icon variant={isDetailsOpen ? IconsEnum.CHEVRON_UP : IconsEnum.CHEVRON_DOWN} />
                                                            </Font.FuturaMedium>
                                                        </a>
                                                    ) : null}
                                                </>
                                            )}
                                        </>
                                    ) : !isAuthenticated ? (
                                        <>
                                            <Button.Default
                                                color={getColorStyle(ColorTypesEnum.WHITE)}
                                                bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                type="button"
                                                label={t('races.preregistration.connect')}
                                                onPress={() => login()}
                                            />
                                            {!prereg.isOpen ? (
                                                <Font.FuturaBook mobile="16" desktop="18" as="p" className={styles.card_information_text}>
                                                    {t('races.preregistration.closed')}
                                                </Font.FuturaBook>
                                            ) : null}
                                        </>
                                    ) : (
                                        <>
                                            <ul>
                                                {!prereg.neededCategory && !prereg.isNeedStones && !prereg.minimumAge && !prereg.isNeedUtmbGo ? (
                                                    <>
                                                        <Font.FuturaMedium as="li" mobile="14" desktop="16">
                                                            {t('races.preregistration.noRestriction')}
                                                        </Font.FuturaMedium>
                                                    </>
                                                ) : null}
                                                {prereg.neededCategory ? (
                                                    <Font.FuturaMedium as="li" mobile="14" desktop="16" className={styles.ctrl}>
                                                        {prereg.categoryStatus === SimpleStatusEnum.OK ? (
                                                            <ImageNext width="24" height="24" alt="OK" src={`/svg/my-utmb-finals/ok.svg`} />
                                                        ) : (
                                                            <ImageNext width="24" height="24" alt="KO" src={`/svg/my-utmb-finals/no.svg`} />
                                                        )}{' '}
                                                        <span>{t('races.preregistration.condition.categ', { category: prereg.neededCategory })}</span>
                                                    </Font.FuturaMedium>
                                                ) : null}
                                                {prereg.isNeedStones ? (
                                                    <Font.FuturaMedium as="li" mobile="14" desktop="16" className={styles.ctrl}>
                                                        {prereg.stonesStatus === SimpleStatusEnum.OK ? (
                                                            <ImageNext width="24" height="24" alt="OK" src={`/svg/my-utmb-finals/ok.svg`} />
                                                        ) : (
                                                            <ImageNext width="24" height="24" alt="KO" src={`/svg/my-utmb-finals/no.svg`} />
                                                        )}{' '}
                                                        <span>{t('races.preregistration.condition.stones')}</span>
                                                    </Font.FuturaMedium>
                                                ) : null}
                                                {prereg.minimumAge ? (
                                                    <Font.FuturaMedium as="li" mobile="14" desktop="16" className={styles.ctrl}>
                                                        {prereg.ageStatus === SimpleStatusEnum.OK ? (
                                                            <ImageNext width="24" height="24" alt="OK" src={`/svg/my-utmb-finals/ok.svg`} />
                                                        ) : (
                                                            <ImageNext width="24" height="24" alt="KO" src={`/svg/my-utmb-finals/no.svg`} />
                                                        )}{' '}
                                                        <span>{t('races.preregistration.condition.age', { age: prereg.minimumAge })}</span>
                                                    </Font.FuturaMedium>
                                                ) : null}
                                                {prereg.isNeedUtmbGo ? (
                                                    <>
                                                        <Font.FuturaMedium as="li" mobile="14" desktop="16" className={styles.ctrl}>
                                                            {prereg.utmbGoStatus === SimpleStatusEnum.OK ? (
                                                                <ImageNext width="24" height="24" alt="OK" src={`/svg/my-utmb-finals/ok.svg`} />
                                                            ) : (
                                                                <ImageNext width="24" height="24" alt="KO" src={`/svg/my-utmb-finals/no.svg`} />
                                                            )}{' '}
                                                            <span>{t('races.preregistration.condition.utmbGo')}</span>
                                                        </Font.FuturaMedium>
                                                        {prereg.boostPct ? (
                                                            <Font.FuturaMedium as="li" mobile="14" desktop="16" className={styles.ctrl}>
                                                                {t(`races.preregistration.${prereg.hasBoost ? 'withBoost' : 'withoutBoost'}`, {
                                                                    pct: prereg.boostPct,
                                                                })}
                                                            </Font.FuturaMedium>
                                                        ) : null}
                                                    </>
                                                ) : null}
                                                {prereg.needCustomCode ? (
                                                    <Font.FuturaMedium as="li" mobile="14" desktop="16" className={styles.ctrl}>
                                                        {prereg.customCodeStatus === SimpleStatusEnum.OK ? (
                                                            <ImageNext width="24" height="24" alt="OK" src={`/svg/my-utmb-finals/ok.svg`} />
                                                        ) : (
                                                            <ImageNext width="24" height="24" alt="KO" src={`/svg/my-utmb-finals/no.svg`} />
                                                        )}{' '}
                                                        <span>{t(`races.preregistration.condition.custom${prereg.needCustomCode}`)}</span>
                                                    </Font.FuturaMedium>
                                                ) : null}
                                            </ul>

                                            {prereg.hasPrio && prereg.url && prereg.canRegister && !formOpen ? (
                                                <div className={styles.wrapButtons}>
                                                    <Button.Default
                                                        color={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                                                        bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                        type="button"
                                                        label={t('races.preregistration.priority.cta')}
                                                        onPress={() => (location.href = prereg.url!)}
                                                    />

                                                    {prereg.isOpen ? (
                                                        <ButtonRemake
                                                            customClassName={styles.buttonLink}
                                                            variant="link"
                                                            action={() => setFormOpen(prereg.raceYearId)}
                                                            label={t('races.preregistration.priority.preferLotery')}
                                                        />
                                                    ) : null}
                                                </div>
                                            ) : (
                                                <>
                                                    {prereg.canRegister && !formOpen ? (
                                                        <Button.Default
                                                            color={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                                                            bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                            type="button"
                                                            label={t('races.preregistration.preregisterButton')}
                                                            onPress={() => setFormOpen(prereg.raceYearId)}
                                                        />
                                                    ) : null}
                                                </>
                                            )}

                                            {!prereg.isOpen ? (
                                                <Font.FuturaBook mobile="16" desktop="18" as="p" className={styles.card_information_text}>
                                                    {t('races.preregistration.closed')}
                                                </Font.FuturaBook>
                                            ) : null}
                                        </>
                                    )}
                                </div>
                            </div>
                            {isDetailsOpen && prereg.registration && (
                                <div className={styles.detail}>
                                    {prereg.registration.status === RegistrationStatusEnum.PREREGISTERED_DRAW_OK ? (
                                        <>
                                            <Font.FuturaBook mobile="16" desktop="18" as="p" className={styles.card_information_text}>
                                                {t('races.preregistration.finalizeExplanations')}
                                            </Font.FuturaBook>
                                            {prereg.registration.grp && (
                                                <>
                                                    <Font.FuturaBook mobile="16" desktop="18" as="p" className={styles.card_information_text}>
                                                        <span
                                                            dangerouslySetInnerHTML={{
                                                                __html: t('races.preregistration.preregisteredWithGroup', { grp: prereg.registration.grp }),
                                                            }}
                                                        />
                                                    </Font.FuturaBook>
                                                    <ul>
                                                        {prereg.registration.otherGroupMembers.map((member) => {
                                                            return (
                                                                <Font.FuturaBook mobile="14" desktop="16" as="li" key={member.name}>
                                                                    {member.name}
                                                                    {prereg.isNeedStones ? ` - ${member.stoneNumber} Running Stones` : ''}
                                                                    {prereg.boostPct
                                                                        ? ` - ${t(
                                                                              `races.preregistration.memberBoost.${
                                                                                  member.hasBoost ? 'withBoost' : 'withoutBoost'
                                                                              }`
                                                                          )}`
                                                                        : ''}
                                                                </Font.FuturaBook>
                                                            );
                                                        })}
                                                    </ul>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {prereg.registration.grp && (
                                                <>
                                                    <Font.FuturaBook mobile="16" desktop="18" as="p" className={styles.card_information_text}>
                                                        <span
                                                            dangerouslySetInnerHTML={{
                                                                __html: t('races.preregistration.preregisteredWithGroup', { grp: prereg.registration.grp }),
                                                            }}
                                                        />
                                                    </Font.FuturaBook>
                                                    <ul>
                                                        {prereg.registration.otherGroupMembers.map((member) => {
                                                            return (
                                                                <Font.FuturaBook mobile="14" desktop="16" as="li" key={member.name}>
                                                                    {member.name}
                                                                    {prereg.isNeedStones ? ` - ${member.stoneNumber} Running Stones` : ''}
                                                                    {prereg.boostPct
                                                                        ? ` - ${t(
                                                                              `races.preregistration.memberBoost.${
                                                                                  member.hasBoost ? 'withBoost' : 'withoutBoost'
                                                                              }`
                                                                          )}`
                                                                        : ''}
                                                                </Font.FuturaBook>
                                                            );
                                                        })}
                                                    </ul>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                            {formOpen && formOpen === prereg.raceYearId ? (
                                <div className={styles.form}>
                                    <PreregistrationForm
                                        item={prereg}
                                        onCancel={() => setFormOpen(0)}
                                        validate={() => {
                                            setFormOpen(0);
                                            getPreregistrations().then();
                                        }}
                                    />
                                </div>
                            ) : null}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
